import { useEffect, useMemo, useState } from "react";
import { apiStaff } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Flag, ChevronRight, ChevronDown, DollarSign, Users, ClipboardList, BarChart3,
  ShieldAlert, Phone, Mail, Inbox, Tag, ArrowUpRight, Receipt, Send, ExternalLink,
  WashingMachine, Refrigerator, Microwave, Wind, Flame, ChefHat, Snowflake, Trash2, Wrench, Droplets,
  ChevronLeft, X, Pencil, Ban, Repeat, UserCog, KeyRound, Trash,
} from "lucide-react";
import {
  PortalShell, TopBar, Sidebar, MobileDrawer, useMobileDrawer, PageContent, PageHeader,
  KpiGrid, Kpi, Card, FilterBar, FilterChip, StatusPill, Table, Tr, Td,
  EmptyState, Loading, BrandMark, downloadCsv,
} from "@/components/admin";
import DispatchBoardDnD from "@/components/admin/DispatchBoardDnD";
import RecurringPlans from "@/components/admin/RecurringPlans";
import { useRealtime } from "@/lib/realtime";
import JobInvoicePanel from "@/components/JobInvoicePanel";
import PaymentSummary from "@/components/PaymentSummary";
import BrandedInvoice from "@/components/BrandedInvoice";
import SchedulePicker, { TIME_BLOCKS as SHARED_TIME_BLOCKS } from "@/components/SchedulePicker";

const APPLIANCES = ["washer","dryer","refrigerator","oven","stove","dishwasher","microwave","disposal","ice-maker","other"];
const APPLIANCE_META = {
  "washer":       { icon: WashingMachine, label: "Washer" },
  "dryer":        { icon: Wind,           label: "Dryer" },
  "refrigerator": { icon: Refrigerator,   label: "Fridge" },
  "oven":         { icon: ChefHat,        label: "Oven" },
  "stove":        { icon: Flame,          label: "Stove" },
  "dishwasher":   { icon: Droplets,       label: "Dishwasher" },
  "microwave":    { icon: Microwave,      label: "Microwave" },
  "disposal":     { icon: Trash2,         label: "Disposal" },
  "ice-maker":    { icon: Snowflake,      label: "Ice maker" },
  "other":        { icon: Wrench,         label: "Other" },
};
// 2-hour service windows — single source of truth now lives in /components/SchedulePicker.jsx
const TIME_BLOCKS = SHARED_TIME_BLOCKS;
const STATUSES = ["new","scheduled","en-route","on-site","part-ordered","completed","cancelled"];

const NAV = [
  { key: "dispatch", label: "Dispatch", icon: ClipboardList },
  { key: "jobs", label: "Jobs", icon: ClipboardList },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "leads", label: "Leads", icon: Inbox },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "techs", label: "Technicians", icon: Users },
  { key: "employees", label: "Employees", icon: UserCog },
  { key: "pricing", label: "Pricing bands", icon: DollarSign },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "audit", label: "Audit log", icon: ShieldAlert },
];

export default function CrmPortal({ info, onLogout }) {
  const [tab, setTab] = useState("dispatch");
  const [counts, setCounts] = useState({});
  useEffect(() => {
    Promise.all([
      apiStaff.get("/crm/jobs").then(r => r.data.length).catch(() => null),
      apiStaff.get("/leads").then(r => r.data.length).catch(() => null),
      apiStaff.get("/technicians").then(r => r.data.length).catch(() => null),
    ]).then(([j, l, t]) => setCounts({ jobs: j, leads: l, techs: t }));
  }, [tab]);

  const navWithCounts = NAV.map((it) => ({
    ...it,
    badge: it.key === "jobs" ? counts.jobs : it.key === "leads" ? counts.leads : it.key === "techs" ? counts.techs : null,
  }));
  const drawer = useMobileDrawer();
  const activeNav = navWithCounts.find((n) => n.key === tab);

  return (
    <PortalShell>
      <TopBar
        brand={<BrandMark label="Space CRM" sublabel={activeNav?.label || "Manager"} />}
        user={info}
        onLogout={onLogout}
        onMenu={drawer.openMenu}
      />
      <MobileDrawer
        open={drawer.open}
        onClose={drawer.closeMenu}
        items={navWithCounts}
        active={tab}
        onSelect={setTab}
        brand={<BrandMark label="Space CRM" sublabel="Manager" />}
        user={info}
        onLogout={onLogout}
      />
      <div className="flex">
        <Sidebar items={navWithCounts} active={tab} onSelect={setTab} />
        <PageContent>
          {tab === "dispatch" && <DispatchBoard />}
          {tab === "jobs" && <JobsTable />}
          {tab === "invoices" && <InvoicesTab />}
          {tab === "leads" && <LeadsTable />}
          {tab === "recurring" && <RecurringPlans />}
          {tab === "techs" && <TechniciansTab />}
          {tab === "employees" && <EmployeesTab info={info} />}
          {tab === "pricing" && <PricingTab />}
          {tab === "analytics" && <AnalyticsTab />}
          {tab === "audit" && <AuditTab />}
        </PageContent>
      </div>
    </PortalShell>
  );
}

// ============== Dispatch board ==============
function DispatchBoard() {
  const [data, setData] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const reload = () => apiStaff.get("/crm/jobs/dispatch").then((r) => setData(r.data));
  useEffect(() => { reload(); }, []);
  useRealtime("staff", ["job."], reload);
  if (!data) return <Loading />;

  return (
    <>
      <PageHeader
        title="Dispatch"
        subtitle={`${data.technicians.length} technicians · drag a job to reassign`}
        actions={<NewJobDialog onCreated={reload} />}
      />
      <DispatchBoardDnD
        data={data}
        role="manager"
        testidPrefix="dispatch"
        onCardClick={setSelectedJob}
        onReload={reload}
      />
      <JobDrawer jobId={selectedJob} onClose={() => setSelectedJob(null)} onChanged={reload} role="manager" />
    </>
  );
}

// ============== Jobs ==============
function JobsTable() {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null); // mobile expand id
  const reload = () => apiStaff.get("/crm/jobs").then(r => setJobs(r.data));
  useEffect(() => { reload(); }, []);
  useRealtime("staff", ["job."], reload);
  const filtered = useMemo(() => jobs.filter((j) => {
    if (status && j.status !== status) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return [j.customer_name, j.customer_phone, j.address, j.city, j.appliance, j.appliance_model].some(v => (v || "").toLowerCase().includes(t));
  }), [jobs, q, status]);

  const exportCsv = () => downloadCsv("jobs.csv", filtered, [
    { label: "ID", get: (r) => r.id },
    { label: "Customer", get: (r) => r.customer_name },
    { label: "Phone", get: (r) => r.customer_phone },
    { label: "Email", get: (r) => r.customer_email || "" },
    { label: "City", get: (r) => r.city || "" },
    { label: "Address", get: (r) => r.address },
    { label: "Appliance", get: (r) => r.appliance },
    { label: "Model", get: (r) => r.appliance_model || "" },
    { label: "Scheduled", get: (r) => r.scheduled_for || "" },
    { label: "Status", get: (r) => r.status },
    { label: "Final Price", get: (r) => r.final_price ?? "" },
    { label: "Quoted", get: (r) => r.quoted_price ?? "" },
    { label: "Created", get: (r) => r.created_at },
  ]);

  async function cancelJob(j, e) {
    e?.stopPropagation();
    if (!window.confirm(`Cancel job for ${j.customer_name}? It will move to the Cancelled filter (kept in DB).`)) return;
    try {
      await apiStaff.post(`/crm/jobs/${j.id}/cancel`);
      toast.success("Job cancelled");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  return (
    <>
      <PageHeader title="Jobs" subtitle={`${filtered.length} of ${jobs.length}`} actions={<NewJobDialog onCreated={reload} />} />
      <FilterBar
        search={q} onSearch={setQ}
        placeholder="Search by name, phone, city, address, appliance, model…"
        filters={<FilterChip label="Status" value={status} options={STATUSES.map(s => ({ value: s, label: s }))} onChange={setStatus} />}
        onExport={exportCsv}
      />

      {/* Desktop: full 11-column table */}
      <div className="hidden lg:block">
        <Card padded={false}>
          <Table
            head={[
              { label: "Customer" }, { label: "Phone" }, { label: "City" }, { label: "Address" },
              { label: "Appliance" }, { label: "Model" }, { label: "Date" }, { label: "Time" },
              { label: "Tech" }, { label: "Status" },
              { label: "Price", align: "right" }, { label: "" },
            ]}
            rows={filtered.map((j) => (
              <Tr key={j.id} onClick={() => setSelectedJob(j.id)} testid={`jobs-row-${j.id}`}>
                <Td><div className="font-medium text-[#002060]" data-testid={`jobs-customer-${j.id}`}>{j.customer_name}</div></Td>
                <Td><span className="text-xs num text-[#002060]/80">{j.customer_phone || "—"}</span></Td>
                <Td><span className="text-xs">{j.city || "—"}</span></Td>
                <Td><span className="text-xs text-[#002060]/70 line-clamp-1 max-w-[180px] inline-block">{j.address || "—"}</span></Td>
                <Td><span className="capitalize text-xs">{j.appliance}</span></Td>
                <Td><span className="text-xs num">{j.appliance_model || "—"}</span></Td>
                <Td><span className="text-xs num">{fmtDate(j.scheduled_for)}</span></Td>
                <Td><span className="text-xs num">{fmtTime(j.scheduled_for)}</span></Td>
                <Td><span className="text-xs text-[#002060]/70">{j.technician_name || (j.technician_id ? "Assigned" : "—")}</span></Td>
                <Td><StatusPill status={j.status} /></Td>
                <Td align="right"><span className="num text-xs font-semibold">{j.final_price != null ? `$${Number(j.final_price).toFixed(0)}` : (j.quoted_price != null ? `~$${Number(j.quoted_price).toFixed(0)}` : "—")}</span></Td>
                <Td align="right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      data-testid={`job-edit-${j.id}`}
                      onClick={(e) => { e.stopPropagation(); setSelectedJob(j.id); }}
                      className="w-7 h-7 grid place-items-center rounded text-[#002060]/60 hover:text-[#FF6600] hover:bg-[#FF6600]/10 transition"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {j.status !== "cancelled" && j.status !== "completed" && (
                      <button
                        data-testid={`job-cancel-${j.id}`}
                        onClick={(e) => cancelJob(j, e)}
                        className="w-7 h-7 grid place-items-center rounded text-[#002060]/60 hover:text-[#FF6600] hover:bg-[#FF6600]/10 transition"
                        title="Cancel job"
                      >
                        <Ban className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
            empty={q || status ? "No jobs match your filters." : "No jobs yet."}
          />
        </Card>
      </div>

      {/* Mobile: compact rows + tap-to-expand */}
      <div className="lg:hidden space-y-2">
        {filtered.length === 0 && <Card><EmptyState message={q || status ? "No jobs match your filters." : "No jobs yet."} /></Card>}
        {filtered.map((j) => {
          const isOpen = expandedRow === j.id;
          return (
            <div key={j.id} className="bg-[#FFFFFF] border border-[#002060]/15 rounded-lg overflow-hidden" data-testid={`jobs-mobile-row-${j.id}`}>
              <button
                type="button"
                onClick={() => setExpandedRow(isOpen ? null : j.id)}
                className="w-full text-left p-3 flex items-center gap-3 hover:bg-[#FF6600]/5 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#002060] truncate">{j.customer_name}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <StatusPill status={j.status} />
                    <span className="text-xs text-[#002060]/60 truncate">{j.appliance}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="num font-semibold text-[#002060]">{j.final_price != null ? `$${Number(j.final_price).toFixed(0)}` : (j.quoted_price != null ? `~$${Number(j.quoted_price).toFixed(0)}` : "—")}</div>
                  <ChevronDown className={`w-4 h-4 text-[#002060]/40 ml-auto transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-[#002060]/10 px-3 py-3 text-xs space-y-1.5 bg-[#FFFFFF]" data-testid={`jobs-mobile-detail-${j.id}`}>
                  <KV2 k="Phone" v={j.customer_phone} numeric />
                  <KV2 k="Email" v={j.customer_email} />
                  <KV2 k="City" v={j.city} />
                  <KV2 k="Address" v={j.address} />
                  <KV2 k="Model" v={j.appliance_model} numeric />
                  <KV2 k="Date" v={fmtDate(j.scheduled_for)} numeric />
                  <KV2 k="Time" v={fmtTime(j.scheduled_for)} numeric />
                  <KV2 k="Tech" v={j.technician_name || (j.technician_id ? "Assigned" : "—")} />
                  <KV2 k="Issue" v={j.issue} />
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" onClick={() => setSelectedJob(j.id)} className="h-8 bg-[#FF6600] hover:bg-[#FF6600] text-white flex-1" data-testid={`job-mobile-edit-${j.id}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    {j.status !== "cancelled" && j.status !== "completed" && (
                      <Button size="sm" variant="outline" onClick={(e) => cancelJob(j, e)} className="h-8 border-[#FF6600] text-[#FF6600] hover:bg-[#FF6600]/10 flex-1" data-testid={`job-mobile-cancel-${j.id}`}>
                        <Ban className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <JobDrawer jobId={selectedJob} onClose={() => setSelectedJob(null)} onChanged={reload} role="manager" />
    </>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch (_e) { return "—"; }
}
function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
  catch (_e) { return "—"; }
}
function KV2({ k, v, numeric }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[#002060]/60 uppercase tracking-wider text-[10px] font-semibold">{k}</span>
      <span className={`text-[#002060] text-right break-words ${numeric ? "num" : ""}`}>{v || "—"}</span>
    </div>
  );
}

// ============== Leads ==============
function LeadsTable() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(null);
  const [editing, setEditing] = useState(null);
  const reload = () => apiStaff.get("/leads").then(r => setLeads(r.data));
  useEffect(() => { reload(); }, []);
  useRealtime("staff", ["lead."], reload);
  const filtered = useMemo(() => leads.filter((l) => {
    if (status && l.status !== status) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return [l.name, l.phone, l.address, l.city, l.appliance, l.model, l.email].some(v => (v || "").toLowerCase().includes(t));
  }), [leads, q, status]);

  const exportCsv = () => downloadCsv("leads.csv", filtered, [
    { label: "Name", get: (r) => r.name }, { label: "Phone", get: (r) => r.phone },
    { label: "Email", get: (r) => r.email || "" },
    { label: "City", get: (r) => r.city || "" }, { label: "Address", get: (r) => r.address || "" },
    { label: "Appliance", get: (r) => r.appliance }, { label: "Model", get: (r) => r.model || "" },
    { label: "Scheduled", get: (r) => r.scheduled_for || r.preferred_window || "" },
    { label: "Status", get: (r) => r.status }, { label: "Source", get: (r) => r.utm_source || "" },
    { label: "Promo", get: (r) => r.promo_code || "" }, { label: "Created", get: (r) => r.created_at },
  ]);

  async function cancelLead(l) {
    if (!window.confirm(`Cancel lead from ${l.name}?`)) return;
    try { await apiStaff.post(`/leads/${l.id}/cancel`); toast.success("Lead cancelled"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  return (
    <>
      <PageHeader title="Leads" subtitle={`${filtered.length} of ${leads.length}`} />
      <FilterBar
        search={q} onSearch={setQ}
        placeholder="Search by name, phone, city, address, appliance, model…"
        filters={<FilterChip label="Status" value={status} options={["new","contacted","converted","lost","cancelled"].map(s => ({ value: s, label: s }))} onChange={setStatus} />}
        onExport={exportCsv}
      />

      {/* Desktop: full table */}
      <div className="hidden lg:block">
        <Card padded={false}>
          <Table
            head={[
              { label: "Name" }, { label: "Phone" }, { label: "Email" }, { label: "City" },
              { label: "Address" }, { label: "Appliance" }, { label: "Model" },
              { label: "Scheduled" }, { label: "Status" }, { label: "Source" }, { label: "" },
            ]}
            rows={filtered.map((l) => (
              <Tr key={l.id} testid={`leads-row-${l.id}`}>
                <Td><span className="font-medium text-[#002060]">{l.name}</span></Td>
                <Td><span className="num text-xs text-[#002060]/80">{l.phone || "—"}</span></Td>
                <Td><span className="text-xs text-[#002060]/70 max-w-[160px] truncate inline-block">{l.email || "—"}</span></Td>
                <Td><span className="text-xs">{l.city || "—"}</span></Td>
                <Td><span className="text-xs text-[#002060]/70 line-clamp-1 max-w-[180px] inline-block">{l.address || "—"}</span></Td>
                <Td><span className="capitalize text-xs">{l.appliance}</span></Td>
                <Td><span className="text-xs num">{l.model || "—"}</span></Td>
                <Td><span className="text-xs num">{l.scheduled_for ? fmtDate(l.scheduled_for) + " " + fmtTime(l.scheduled_for) : (l.preferred_window || "—")}</span></Td>
                <Td><StatusPill tone={l.status === "converted" ? "emerald" : l.status === "lost" || l.status === "cancelled" ? "slate" : "blue"} label={l.status} /></Td>
                <Td><span className="text-[11px] text-[#002060]/60">{l.utm_source || "direct"}</span></Td>
                <Td align="right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      data-testid={`lead-edit-${l.id}`}
                      onClick={() => setEditing(l)}
                      className="w-7 h-7 grid place-items-center rounded text-[#002060]/60 hover:text-[#FF6600] hover:bg-[#FF6600]/10 transition"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {l.status !== "converted" && (
                      <NewJobDialog
                        leadPrefill={l} onCreated={reload}
                        trigger={
                          <button data-testid={`lead-convert-${l.id}`} className="w-7 h-7 grid place-items-center rounded text-[#FF6600] hover:bg-[#FF6600]/10 transition" title="Convert to job">
                            <ArrowUpRight className="w-3.5 h-3.5" />
                          </button>
                        }
                      />
                    )}
                    {l.status !== "cancelled" && l.status !== "converted" && (
                      <button
                        data-testid={`lead-cancel-${l.id}`}
                        onClick={() => cancelLead(l)}
                        className="w-7 h-7 grid place-items-center rounded text-[#002060]/60 hover:text-[#FF6600] hover:bg-[#FF6600]/10 transition"
                        title="Cancel lead"
                      >
                        <Ban className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
            empty={q || status ? "No leads match." : "No leads yet."}
          />
        </Card>
      </div>

      {/* Mobile cards (existing pattern, with new Edit / Cancel) */}
      <div className="lg:hidden grid gap-3">
        {filtered.map((l) => (
          <div key={l.id} className="bg-[#FFFFFF] border border-[#002060]/15 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium capitalize truncate">{l.appliance}{l.model ? ` · ${l.model}` : ""}</div>
                <div className="text-xs text-[#002060]/70 mt-0.5 line-clamp-2">{l.issue || "—"}</div>
              </div>
              <StatusPill tone={l.status === "converted" ? "emerald" : l.status === "lost" || l.status === "cancelled" ? "slate" : "blue"} label={l.status} />
            </div>
            <div className="mt-3 text-xs text-[#002060]">
              <div className="font-medium">{l.name}</div>
              <div className="text-[#002060]/70 num">{l.phone}</div>
              <div className="text-[#002060]/50 truncate mt-0.5">{l.city ? `${l.city} · ` : ""}{l.address || "—"}</div>
              {l.utm_source && <div className="text-[11px] text-[#002060]/50 mt-1">↳ {l.utm_source}{l.utm_campaign ? ` / ${l.utm_campaign}` : ""}</div>}
              {l.promo_code && <div className="text-[11px] text-[#FF6600] mt-1 flex items-center gap-1"><Tag className="w-3 h-3" />{l.promo_code}</div>}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setEditing(l)} className="bg-[#FF6600] hover:bg-[#FF6600] text-white h-8" data-testid={`lead-mobile-edit-${l.id}`}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
              {l.status !== "converted" && (
                <NewJobDialog
                  leadPrefill={l} onCreated={reload}
                  trigger={<Button data-testid={`lead-mobile-convert-${l.id}`} size="sm" variant="outline" className="h-8 border-[#FF6600] text-[#FF6600] hover:bg-[#FF6600]/10">Convert</Button>}
                />
              )}
              {l.status !== "cancelled" && l.status !== "converted" && (
                <Button size="sm" variant="outline" onClick={() => cancelLead(l)} className="h-8 border-[#002060]/30 text-[#002060]/70" data-testid={`lead-mobile-cancel-${l.id}`}>
                  <Ban className="w-3 h-3 mr-1" /> Cancel
                </Button>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <Card><EmptyState message={q || status ? "No leads match." : "No leads yet."} /></Card>}
      </div>

      <LeadEditDialog lead={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
    </>
  );
}

function LeadEditDialog({ lead, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!lead) return;
    setForm({
      name: lead.name || "", phone: lead.phone || "", email: lead.email || "",
      city: lead.city || "", address: lead.address || "",
      appliance: lead.appliance || "", model: lead.model || "",
      issue: lead.issue || "", preferred_window: lead.preferred_window || "",
      scheduled_for: lead.scheduled_for || "", status: lead.status || "new",
    });
  }, [lead]);
  if (!lead) return null;

  async function save() {
    try {
      await apiStaff.patch(`/leads/${lead.id}`, form);
      toast.success("Lead updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit lead</DialogTitle>
          <DialogDescription className="text-[#002060]/70">All fields are editable. Use Status to flip between new/contacted/converted/lost/cancelled.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3" data-testid="lead-edit-form">
          <Mini label="Name"><Input data-testid="lead-edit-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Mini>
          <Mini label="Phone"><Input data-testid="lead-edit-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Mini>
          <div className="col-span-2"><Mini label="Email"><Input data-testid="lead-edit-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Mini></div>
          <Mini label="City"><Input data-testid="lead-edit-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Mini>
          <Mini label="Address"><Input data-testid="lead-edit-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Mini>
          <Mini label="Appliance">
            <Select value={form.appliance || ""} onValueChange={(v) => setForm({ ...form, appliance: v })}>
              <SelectTrigger data-testid="lead-edit-appliance"><SelectValue /></SelectTrigger>
              <SelectContent>{["washer","dryer","refrigerator","oven","stove","dishwasher","microwave","disposal","ice-maker","other"].map(a => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}</SelectContent>
            </Select>
          </Mini>
          <Mini label="Model"><Input data-testid="lead-edit-model" value={form.model || ""} onChange={(e) => setForm({ ...form, model: e.target.value })} /></Mini>
          <Mini label="Preferred window"><Input data-testid="lead-edit-window" value={form.preferred_window || ""} onChange={(e) => setForm({ ...form, preferred_window: e.target.value })} placeholder="Tue 10–12" /></Mini>
          <Mini label="Status">
            <Select value={form.status || "new"} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="lead-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["new","contacted","converted","lost","cancelled"].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </Mini>
          <div className="col-span-2"><Mini label="Issue"><Textarea data-testid="lead-edit-issue" rows={2} value={form.issue || ""} onChange={(e) => setForm({ ...form, issue: e.target.value })} /></Mini></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="lead-edit-cancel">Cancel</Button>
          <Button onClick={save} data-testid="lead-edit-save" className="bg-[#FF6600] hover:bg-[#FF6600] text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Technicians ==============
function TechniciansTab() {
  const [techs, setTechs] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const reload = () => apiStaff.get("/technicians").then(r => setTechs(r.data));
  useEffect(() => { reload(); }, []);
  return (
    <>
      <PageHeader
        title="Technicians" subtitle={`${techs.length} on roster`}
        actions={<Button data-testid="new-tech-btn" onClick={() => setShowNew(true)} className="bg-[#FF6600] hover:bg-[#FF6600] text-white h-9"><Plus className="w-4 h-4 mr-1.5" /> Add tech</Button>}
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {techs.map(t => (
          <div key={t.id} className="bg-[#FFFFFF] border border-[#002060]/15 rounded-lg p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="font-medium truncate">{t.name}</div>
              <StatusPill tone={t.on_duty ? "emerald" : "slate"} label={t.on_duty ? "on duty" : "off"} />
            </div>
            <div className="text-xs text-[#002060]/70 mt-1">{t.phone}{t.email ? ` · ${t.email}` : ""}</div>
            {t.active_jobs > 0 && <div className="mt-1.5 text-[11px] font-semibold text-[#FF6600]">{t.active_jobs} active job{t.active_jobs === 1 ? "" : "s"}</div>}
            {t.skills?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{t.skills.map(s => <span key={s} className="text-[10px] px-1.5 py-0.5 bg-[#FFFFFF] rounded text-[#002060]/80">{s}</span>)}</div>}
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" className="h-8" data-testid={`tech-toggle-${t.id}`} onClick={async () => { await apiStaff.post(`/technicians/${t.id}/toggle-duty`); reload(); }}>Toggle duty</Button>
              <Button size="sm" variant="outline" className="h-8 border-rose-300 text-rose-600 hover:bg-rose-50" aria-label={`Delete technician ${t.name}`} data-testid={`tech-delete-${t.id}`} onClick={() => setDelTarget(t)}><Trash className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      {techs.length === 0 && <Card><EmptyState message="No technicians yet." action={<Button onClick={() => setShowNew(true)} className="bg-[#FF6600] text-white">Add first tech</Button>} /></Card>}
      <NewTechDialog open={showNew} onOpenChange={setShowNew} onCreated={reload} />
      <DeleteTechDialog tech={delTarget} allTechs={techs} onClose={() => setDelTarget(null)} onDone={reload} />
    </>
  );
}

function NewTechDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", pin: "", skills: "" });
  async function submit() {
    try {
      await apiStaff.post("/technicians", {
        name: form.name, phone: form.phone, email: form.email || undefined,
        pin: form.pin, skills: form.skills.split(",").map(s => s.trim()).filter(Boolean), on_duty: true,
      });
      toast.success("Technician added");
      onOpenChange(false); onCreated && onCreated();
      setForm({ name: "", phone: "", email: "", pin: "", skills: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New technician</DialogTitle><DialogDescription>Add a field technician who can log into the tech app with their phone &amp; PIN.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Mini label="Name"><Input data-testid="newtech-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Mini>
          <Mini label="Phone"><Input data-testid="newtech-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Mini>
          <Mini label="Email"><Input data-testid="newtech-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Mini>
          <Mini label="4-digit PIN"><Input data-testid="newtech-pin" maxLength={4} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} /></Mini>
          <Mini label="Skills (comma-sep)"><Input data-testid="newtech-skills" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="washer, dryer" /></Mini>
        </div>
        <DialogFooter><Button data-testid="newtech-submit" onClick={submit} className="bg-[#FF6600] hover:bg-[#FF6600] text-white">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteTechDialog({ tech, allTechs, onClose, onDone }) {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTarget(""); }, [tech]);
  if (!tech) return null;
  const hasJobs = (tech.active_jobs || 0) > 0;
  const others = (allTechs || []).filter(t => t.id !== tech.id);
  async function confirm() {
    setBusy(true);
    try {
      const url = hasJobs
        ? `/technicians/${tech.id}?reassign_to=${encodeURIComponent(target)}`
        : `/technicians/${tech.id}`;
      const res = await apiStaff.delete(url);
      const n = res.data?.reassigned || 0;
      toast.success(n > 0 ? `Reassigned ${n} job${n === 1 ? "" : "s"} and deleted ${tech.name}` : `${tech.name} deleted`);
      onClose(); onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
    finally { setBusy(false); }
  }
  const canConfirm = !busy && (!hasJobs || (target && target !== tech.id));
  return (
    <Dialog open={!!tech} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-[#FFFFFF]">
        <DialogHeader>
          <DialogTitle className="text-[#002060]">Delete {tech.name}?</DialogTitle>
          <DialogDescription>
            {hasJobs
              ? `${tech.name} has ${tech.active_jobs} active job${tech.active_jobs === 1 ? "" : "s"}. Pick a technician to move them to, then delete.`
              : "This technician has no active jobs. This can't be undone."}
          </DialogDescription>
        </DialogHeader>
        {hasJobs && (
          <Mini label="Reassign active jobs to">
            {others.length === 0 ? (
              <p className="text-sm text-rose-600">No other technician available to reassign to. Add one first, or close the jobs.</p>
            ) : (
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="reassign-target"><SelectValue placeholder="Choose a technician…" /></SelectTrigger>
                <SelectContent>
                  {others.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}{t.on_duty ? "" : " (off duty)"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Mini>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="deltech-cancel">Cancel</Button>
          <Button
            data-testid="deltech-confirm"
            disabled={!canConfirm}
            onClick={confirm}
            className="bg-rose-600 hover:bg-rose-600 text-white disabled:opacity-40"
          >
            {busy ? "Working…" : hasJobs ? "Reassign & delete" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Employees (staff accounts) ==============
function EmployeesTab({ info }) {
  const [emps, setEmps] = useState([]);
  const [loadErr, setLoadErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const myEmail = (info?.email || "").toLowerCase();
  const reload = () => {
    setLoading(true); setLoadErr(false);
    return apiStaff.get("/staff/employees")
      .then(r => { setEmps(r.data); setLoadErr(false); })
      .catch(() => setLoadErr(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  async function toggleActive(e) {
    if (e.email.toLowerCase() === myEmail) { toast.error("You can't disable your own account."); return; }
    try { await apiStaff.patch(`/staff/employees/${e.id}`, { active: !e.active }); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }
  async function remove(e) {
    if (e.email.toLowerCase() === myEmail) { toast.error("You can't delete your own account."); return; }
    if (!window.confirm(`Remove ${e.name} (${e.email})? They will no longer be able to log in.`)) return;
    try { await apiStaff.delete(`/staff/employees/${e.id}`); toast.success("Employee removed"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  return (
    <>
      <PageHeader
        title="Employees"
        subtitle={loadErr ? "Couldn't load employees" : `${emps.length} staff account${emps.length === 1 ? "" : "s"} · CRM & Dispatch login`}
        actions={<Button data-testid="new-emp-btn" onClick={() => setShowNew(true)} className="bg-[#FF6600] hover:bg-[#FF6600] text-white h-9"><Plus className="w-4 h-4 mr-1.5" /> Add employee</Button>}
      />
      {loadErr ? (
        <Card>
          <div className="py-10 text-center" data-testid="emp-load-error">
            <p className="text-sm text-rose-600 font-medium">Couldn't load employees. Check your connection and try again.</p>
            <Button onClick={reload} className="mt-4 bg-[#002060] hover:bg-[#002060] text-white h-9" data-testid="emp-retry">Retry</Button>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {emps.map(e => {
              const isSelf = e.email.toLowerCase() === myEmail;
              return (
              <div key={e.id} className="bg-[#FFFFFF] border border-[#002060]/15 rounded-lg p-4" data-testid={`emp-card-${e.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium truncate text-[#002060]">{e.name}{isSelf && <span className="ml-1.5 text-[10px] text-[#FF6600] font-bold">(you)</span>}</div>
                  <StatusPill tone={e.active ? "emerald" : "slate"} label={e.active ? "active" : "disabled"} />
                </div>
                <div className="text-xs text-[#002060]/70 mt-1 truncate">{e.email}</div>
                <div className="mt-1.5">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${e.role === "manager" ? "border-[#FF6600] text-[#FF6600]" : "border-[#002060]/30 text-[#002060]/70"}`}>
                    {e.role}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="h-8 border-[#002060]/20 text-[#002060]" data-testid={`emp-reset-${e.id}`} onClick={() => setResetFor(e)}>
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset password
                  </Button>
                  <Button size="sm" variant="outline" disabled={isSelf} className="h-8 border-[#002060]/20 text-[#002060] disabled:opacity-40" data-testid={`emp-toggle-${e.id}`} onClick={() => toggleActive(e)}>
                    {e.active ? "Disable" : "Enable"}
                  </Button>
                  <Button size="sm" variant="outline" disabled={isSelf} className="h-8 border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-40" data-testid={`emp-delete-${e.id}`} onClick={() => remove(e)}>
                    <Trash className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );})}
          </div>
          {!loading && emps.length === 0 && <Card><EmptyState message="No employees yet." action={<Button onClick={() => setShowNew(true)} className="bg-[#FF6600] text-white">Add first employee</Button>} /></Card>}
        </>
      )}
      <NewEmployeeDialog open={showNew} onOpenChange={setShowNew} onCreated={reload} />
      <ResetPasswordDialog employee={resetFor} onClose={() => setResetFor(null)} />
    </>
  );
}

function NewEmployeeDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "dispatcher" });
  async function submit() {
    try {
      await apiStaff.post("/staff/employees", {
        name: form.name.trim(), email: form.email.trim(), password: form.password, role: form.role,
      });
      toast.success("Employee added");
      onOpenChange(false); onCreated && onCreated();
      setForm({ name: "", email: "", password: "", role: "dispatcher" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  const canSubmit = form.name && form.email && form.password.length >= 4;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#FFFFFF]">
        <DialogHeader>
          <DialogTitle className="text-[#002060]">Add employee</DialogTitle>
          <DialogDescription>They sign in to the CRM or Dispatch portal with this email &amp; password. No verification step.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Mini label="Full name"><Input data-testid="newemp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Mini>
          <Mini label="Email (their Gmail)"><Input data-testid="newemp-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@gmail.com" /></Mini>
          <Mini label="Password (min 4 chars)"><Input data-testid="newemp-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Set a password" /></Mini>
          <Mini label="Role">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="newemp-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dispatcher">Dispatcher → Dispatch portal</SelectItem>
                <SelectItem value="manager">Manager → CRM (full access)</SelectItem>
              </SelectContent>
            </Select>
          </Mini>
        </div>
        <DialogFooter>
          <Button data-testid="newemp-submit" disabled={!canSubmit} onClick={submit} className="bg-[#FF6600] hover:bg-[#FF6600] text-white">Create account</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ employee, onClose }) {
  const [pw, setPw] = useState("");
  useEffect(() => { setPw(""); }, [employee]);
  async function submit() {
    try {
      await apiStaff.patch(`/staff/employees/${employee.id}`, { password: pw });
      toast.success(`Password reset for ${employee.name}`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={!!employee} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm bg-[#FFFFFF]">
        <DialogHeader>
          <DialogTitle className="text-[#002060]">Reset password</DialogTitle>
          <DialogDescription>{employee ? `Set a new password for ${employee.name} (${employee.email}).` : ""}</DialogDescription>
        </DialogHeader>
        <Mini label="New password (min 4 chars)">
          <Input data-testid="reset-pw-input" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" />
        </Mini>
        <DialogFooter>
          <Button data-testid="reset-pw-submit" disabled={pw.length < 4} onClick={submit} className="bg-[#FF6600] hover:bg-[#FF6600] text-white">Save new password</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============== Pricing ==============
function PricingTab() {
  const [bands, setBands] = useState([]);
  const reload = () => apiStaff.get("/pricing/bands").then(r => setBands(r.data));
  useEffect(() => { reload(); }, []);
  async function save(b) {
    try {
      await apiStaff.put(`/pricing/bands/${b.appliance}?low=${b.low}&high=${b.high}&note=${encodeURIComponent(b.note || "")}`);
      toast.success("Saved"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  return (
    <>
      <PageHeader title="Pricing bands" subtitle="Visible to dispatchers in the price guide." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {bands.map((b, i) => (
          <div key={b.appliance} className="bg-[#FFFFFF] border border-[#002060]/15 rounded-lg p-4">
            <div className="font-medium capitalize">{b.appliance}</div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Mini label="Low ($)"><Input data-testid={`band-low-${b.appliance}`} type="number" value={b.low} onChange={(e) => setBands(bands.map((x, j) => j === i ? { ...x, low: parseFloat(e.target.value || 0) } : x))} /></Mini>
              <Mini label="High ($)"><Input data-testid={`band-high-${b.appliance}`} type="number" value={b.high} onChange={(e) => setBands(bands.map((x, j) => j === i ? { ...x, high: parseFloat(e.target.value || 0) } : x))} /></Mini>
            </div>
            <Mini label="Note"><Input data-testid={`band-note-${b.appliance}`} value={b.note || ""} onChange={(e) => setBands(bands.map((x, j) => j === i ? { ...x, note: e.target.value } : x))} /></Mini>
            <Button data-testid={`band-save-${b.appliance}`} size="sm" className="mt-3 bg-[#FF6600] hover:bg-[#FF6600] text-white h-8" onClick={() => save(b)}>Save</Button>
          </div>
        ))}
      </div>
    </>
  );
}

// ============== Analytics ==============
function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { apiStaff.get("/admin/analytics/overview").then(r => setData(r.data)); }, []);
  if (!data) return <Loading />;
  return (
    <>
      <PageHeader title="Analytics" subtitle="Last 90 days · live" />
      <KpiGrid>
        <Kpi label="Revenue (total)" value={`$${Number(data.revenue_total).toFixed(0)}`} />
        <Kpi label="Gross profit" value={`$${Number(data.gross_profit).toFixed(0)}`} />
        <Kpi label="Avg ticket" value={`$${Number(data.avg_ticket).toFixed(0)}`} />
        <Kpi label="7-day revenue" value={`$${Number(data.last7_revenue).toFixed(0)}`} />
        <Kpi label="Total jobs" value={data.total_jobs} />
        <Kpi label="Completed" value={data.completed_jobs} />
        <Kpi label="Total leads" value={data.total_leads} />
        <Kpi label="Open jobs" value={(data.by_status?.new || 0) + (data.by_status?.scheduled || 0)} />
      </KpiGrid>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="By appliance">
          {Object.entries(data.by_appliance).map(([k, v]) => <Row key={k} k={k} v={v} />)}
          {Object.keys(data.by_appliance).length === 0 && <EmptyState message="No data yet." />}
        </Card>
        <Card title="Lead sources">
          {Object.entries(data.by_source).map(([k, v]) => <Row key={k} k={k} v={v} />)}
          {Object.keys(data.by_source).length === 0 && <EmptyState message="No source data yet." />}
        </Card>
        <Card title="Top technicians (revenue)" className="lg:col-span-2">
          {data.leaderboard.map((t) => <Row key={t.tech_id} k={t.name} v={`$${Number(t.revenue).toFixed(0)}`} />)}
          {data.leaderboard.length === 0 && <EmptyState message="No completed jobs yet." />}
        </Card>
      </div>
    </>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between text-sm py-1.5 border-b border-[#002060]/10 last:border-0"><span className="capitalize text-[#002060]">{k}</span><span className="num font-medium text-[#002060]">{v}</span></div>;
}

// ============== Audit ==============
function AuditTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { apiStaff.get("/admin/audit-log").then(r => setItems(r.data)); }, []);
  const filtered = items.filter((e) => !q || JSON.stringify(e).toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <PageHeader title="Audit log" subtitle={`${filtered.length} of ${items.length} entries`} />
      <FilterBar search={q} onSearch={setQ} placeholder="Search events…" />
      <Card padded={false}>
        <Table
          head={[{ label: "Time" }, { label: "Event" }, { label: "Actor" }, { label: "Details" }]}
          rows={filtered.map((e, i) => (
            <Tr key={i}>
              <Td><span className="text-xs text-[#002060]/70">{new Date(e.ts).toLocaleString()}</span></Td>
              <Td><span className="font-medium text-xs">{e.type}</span></Td>
              <Td><span className="text-xs">{e.actor_email || "—"}</span><span className="text-[10px] text-[#002060]/50 ml-1">({e.actor_role || "anon"})</span></Td>
              <Td><code className="text-[10px] text-[#002060]/70 font-mono break-all">{JSON.stringify(Object.fromEntries(Object.entries(e).filter(([k]) => !["ts","type","actor_email","actor_role"].includes(k))))}</code></Td>
            </Tr>
          ))}
          empty="No audit events."
        />
      </Card>
    </>
  );
}

// ============== Mini field helper ==============
function Mini({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-[#002060]/70 font-medium">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ============== New Job Dialog (unchanged behavior, polished UI) ==============
export function NewJobDialog({ onCreated, leadPrefill, trigger, open: openProp, onOpenChange }) {
  const [openInternal, setOpenInternal] = useState(false);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? openProp : openInternal;
  const setOpen = (v) => { if (isControlled) onOpenChange?.(v); else setOpenInternal(v); };
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "", address: "",
    appliance: "washer", issue: "", scheduled_for: "", technician_id: "",
    lead_id: leadPrefill?.id || "",
  });
  const [techs, setTechs] = useState([]);
  useEffect(() => { apiStaff.get("/technicians").then(r => setTechs(r.data)); }, []);
  useEffect(() => {
    if (leadPrefill && open) {
      setForm((f) => ({ ...f,
        customer_name: leadPrefill.name || "", customer_phone: leadPrefill.phone || "",
        customer_email: leadPrefill.email || "", address: leadPrefill.address || "",
        appliance: leadPrefill.appliance || "washer", issue: leadPrefill.issue || "",
        scheduled_for: leadPrefill.scheduled_for || f.scheduled_for || "",
        technician_id: leadPrefill.technician_id || f.technician_id || "",
        lead_id: leadPrefill.id || "",
      }));
    } else if (!leadPrefill && open) {
      // Fresh new-job opened with no prefill — clear any leftover form state.
      setForm({
        customer_name: "", customer_phone: "", customer_email: "", address: "",
        appliance: "washer", issue: "", scheduled_for: "", technician_id: "",
        lead_id: "",
      });
    }
  }, [leadPrefill, open]);

  const canSubmit = form.customer_name && form.customer_phone && form.address && form.appliance;

  async function submit() {
    const payload = { ...form };
    if (!payload.technician_id || payload.technician_id === "__none__") delete payload.technician_id;
    if (!payload.scheduled_for) delete payload.scheduled_for;
    if (!payload.customer_email) delete payload.customer_email;
    if (!payload.lead_id) delete payload.lead_id;
    try {
      await apiStaff.post("/crm/jobs", payload);
      toast.success("Job created"); setOpen(false); onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button data-testid="new-job-btn" className="bg-[#FF6600] hover:bg-[#FF6600] text-white h-9"><Plus className="w-4 h-4 mr-1.5" /> New job</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] p-0 overflow-hidden flex flex-col bg-[#FFFFFF]">
        <DialogHeader className="px-5 sm:px-7 pt-5 pb-3 border-b border-[#002060]/10 shrink-0">
          <DialogTitle className="text-xl text-[#002060]">New job</DialogTitle>
          <DialogDescription className="text-[#002060]/70 text-sm">
            Capture the customer, choose an appliance, and pick a service window.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 space-y-6">
          {/* Customer block */}
          <section>
            <SectionLabel>Customer</SectionLabel>
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              <Mini label="Name *"><Input data-testid="newjob-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="border-[#002060]/20 focus-visible:ring-[#FF6600] focus-visible:border-[#FF6600]" /></Mini>
              <Mini label="Phone *"><Input data-testid="newjob-phone" inputMode="tel" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="border-[#002060]/20 focus-visible:ring-[#FF6600] focus-visible:border-[#FF6600] num" /></Mini>
              <Mini label="Email (optional)"><Input data-testid="newjob-email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="border-[#002060]/20 focus-visible:ring-[#FF6600] focus-visible:border-[#FF6600]" /></Mini>
              <Mini label="Address *"><Input data-testid="newjob-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border-[#002060]/20 focus-visible:ring-[#FF6600] focus-visible:border-[#FF6600]" /></Mini>
            </div>
          </section>

          {/* Appliance tile grid */}
          <section>
            <SectionLabel>Appliance *</SectionLabel>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2" data-testid="newjob-appliance-grid">
              {APPLIANCES.map((a) => {
                const meta = APPLIANCE_META[a];
                const Icon = meta.icon;
                const selected = form.appliance === a;
                return (
                  <button
                    key={a}
                    type="button"
                    data-testid={`newjob-appliance-${a}`}
                    onClick={() => setForm((f) => ({ ...f, appliance: a }))}
                    className={`flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-lg border-2 transition-all ${
                      selected
                        ? "border-[#FF6600] bg-[#FF6600]/10 text-[#FF6600]"
                        : "border-[#002060]/15 bg-[#FFFFFF] text-[#002060]/70 hover:border-[#002060]/40 hover:text-[#002060]"
                    }`}
                  >
                    <Icon className={`w-6 h-6 ${selected ? "stroke-[2.5]" : ""}`} />
                    <span className={`text-[11px] font-semibold uppercase tracking-wide ${selected ? "" : ""}`}>{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Issue */}
          <section>
            <SectionLabel>Issue (optional)</SectionLabel>
            <Textarea
              data-testid="newjob-issue"
              rows={2}
              value={form.issue}
              onChange={(e) => setForm({ ...form, issue: e.target.value })}
              placeholder="Briefly describe the problem"
              className="mt-2 border-[#002060]/20 focus-visible:ring-[#FF6600] focus-visible:border-[#FF6600]"
            />
          </section>

          {/* Scheduling — day strip + 2-hour blocks */}
          <section>
            <SectionLabel>When (optional)</SectionLabel>
            <SchedulePicker
              value={form.scheduled_for}
              onChange={(iso) => setForm((f) => ({ ...f, scheduled_for: iso }))}
            />
          </section>

          {/* Assign tech — chip strip */}
          <section>
            <SectionLabel>Assign tech</SectionLabel>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="newjob-tech-chips">
              <TechChip
                selected={!form.technician_id || form.technician_id === "__none__"}
                onClick={() => setForm((f) => ({ ...f, technician_id: "" }))}
                label="Unassigned"
                testid="newjob-tech-unassigned"
              />
              {techs.map((t) => (
                <TechChip
                  key={t.id}
                  selected={form.technician_id === t.id}
                  onClick={() => setForm((f) => ({ ...f, technician_id: t.id }))}
                  label={t.name}
                  testid={`newjob-tech-${t.id}`}
                />
              ))}
              {techs.length === 0 && (
                <span className="text-xs text-[#002060]/50 italic">No technicians on staff yet.</span>
              )}
            </div>
          </section>
        </div>

        {/* Sticky footer */}
        <DialogFooter className="px-5 sm:px-7 py-4 border-t border-[#002060]/10 bg-[#FFFFFF] shrink-0">
          <Button
            data-testid="newjob-submit"
            onClick={submit}
            disabled={!canSubmit}
            className="w-full h-12 bg-[#FF6600] hover:bg-[#FF6600]/90 text-white font-bold uppercase tracking-widest text-sm rounded-md disabled:opacity-40"
          >
            Create job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionLabel({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#002060]/70">{children}</div>;
}

function TechChip({ selected, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 h-9 rounded-full border-2 text-sm font-semibold transition-all ${
        selected
          ? "border-[#FF6600] bg-[#FF6600] text-[#FFFFFF]"
          : "border-[#002060]/20 bg-[#FFFFFF] text-[#002060] hover:border-[#002060]/50"
      }`}
    >
      {label}
    </button>
  );
}


// ============== Invoices tab ==============
function InvoicesTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const reload = () => apiStaff.get("/crm/invoices").then((r) => setItems(r.data));
  useEffect(() => { reload(); }, []);
  useRealtime("staff", ["invoice.", "payment."], reload);

  async function sendInvoice(inv) {
    try {
      await apiStaff.post(`/crm/invoices/${inv.id}/send`);
      toast.success(`Sent to ${inv.customer_email}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  if (items === null) return <Loading />;
  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  const totalPaid = items.filter(i => i.status === "paid").reduce((s, i) => s + Number(i.total || 0), 0);
  const totalOpen = items.filter(i => i.status === "draft" || i.status === "sent").reduce((s, i) => s + Number(i.total || 0), 0);

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle={`${items.length} total · created from job drawers`}
      />
      <KpiGrid>
        <Kpi label="Paid (lifetime)" value={`$${totalPaid.toFixed(0)}`} />
        <Kpi label="Open" value={`$${totalOpen.toFixed(0)}`} />
        <Kpi label="Invoices" value={items.length} />
      </KpiGrid>
      <div className="bg-[#FFFFFF] border border-[#002060]/15 rounded-lg px-3 py-2 flex flex-wrap gap-1.5">
        {["all", "draft", "sent", "paid", "void"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`invoice-filter-${s}`}
            className={`px-3 py-1.5 rounded text-xs capitalize transition-colors ${
              filter === s
                ? "bg-[#FF6600] text-white"
                : "text-[#002060]/80 hover:bg-[#FFFFFF] border border-[#002060]/15"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <Card><EmptyState message="No invoices match." /></Card>
      ) : (
        <Card padded={false}>
          <Table
            head={[
              { label: "#" },
              { label: "Customer" },
              { label: "Created" },
              { label: "Total", align: "right" },
              { label: "Status" },
              { label: "Actions", align: "right" },
            ]}
            rows={filtered.map((inv) => (
              <Tr key={inv.id} testid={`inv-row-${inv.id}`}>
                <Td className="num text-[#002060]/70 text-xs">{inv.invoice_number}</Td>
                <Td>
                  <div className="font-medium truncate">{inv.customer_name}</div>
                  <div className="text-xs text-[#002060]/70 truncate">{inv.customer_email || inv.customer_phone || "—"}</div>
                </Td>
                <Td className="text-xs text-[#002060]/70">{new Date(inv.created_at).toLocaleDateString()}</Td>
                <Td align="right" className="num font-semibold">${Number(inv.total).toFixed(2)}</Td>
                <Td>
                  <StatusPill
                    tone={inv.status === "paid" ? "emerald" : inv.status === "sent" ? "blue" : inv.status === "void" ? "slate" : "amber"}
                    label={inv.status}
                  />
                </Td>
                <Td align="right">
                  <div className="inline-flex items-center gap-1">
                    <a
                      href={inv.pay_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`inv-open-${inv.id}`}
                      className="inline-flex items-center justify-center w-7 h-7 rounded text-[#002060]/70 hover:text-[#FF6600] hover:bg-[#FF6600]/10 transition"
                      title="Open pay-link"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    {inv.status !== "paid" && inv.status !== "void" && inv.customer_email && (
                      <button
                        onClick={() => sendInvoice(inv)}
                        data-testid={`inv-send-${inv.id}`}
                        className="inline-flex items-center justify-center w-7 h-7 rounded text-[#002060]/70 hover:text-[#FF6600] hover:bg-[#FF6600]/10 transition"
                        title={inv.status === "sent" ? "Re-send email" : "Email pay-link"}
                      >
                        <Send className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          />
        </Card>
      )}
    </>
  );
}

// ============== Job drawer (unchanged behavior, polished) ==============
export function JobDrawer({ jobId, onClose, onChanged, role }) {
  const [job, setJob] = useState(null);
  const [techs, setTechs] = useState([]);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [latestInvoice, setLatestInvoice] = useState(null);
  useEffect(() => {
    if (!jobId) { setJob(null); setLatestInvoice(null); return; }
    apiStaff.get(`/crm/jobs/${jobId}`).then(r => setJob(r.data));
    apiStaff.get("/technicians").then(r => setTechs(r.data));
    if (role === "manager") {
      apiStaff.get(`/crm/jobs/${jobId}/invoices`).then(r => {
        const list = (r.data || []).filter((i) => i.status !== "void");
        setLatestInvoice(list[0] || null);
      }).catch(() => setLatestInvoice(null));
    }
  }, [jobId, role]);
  if (!jobId) return null;
  const isManager = role === "manager";
  async function update(patch) {
    try {
      const res = await apiStaff.patch(`/crm/jobs/${jobId}`, patch);
      setJob(res.data); onChanged && onChanged(); toast.success("Updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  function reloadInvoice() {
    apiStaff.get(`/crm/jobs/${jobId}`).then(r => setJob(r.data));
    if (role !== "manager") return;
    apiStaff.get(`/crm/jobs/${jobId}/invoices`).then(r => {
      const list = (r.data || []).filter((i) => i.status !== "void");
      setLatestInvoice(list[0] || null);
    }).catch(() => {});
  }
  async function flag() {
    await apiStaff.post(`/crm/jobs/${jobId}/manager-flag`);
    toast.success("Flagged"); setJob({ ...job, manager_flag: true }); onChanged && onChanged();
  }
  async function recordManual(method) {
    const amt = parseFloat(prompt(`Manual ${method} payment ($):`));
    if (isNaN(amt) || amt <= 0) return;
    try {
      await apiStaff.post(`/crm/jobs/${jobId}/invoice/manual-payment?amount=${amt}&method=${method}`);
      toast.success("Payment recorded"); apiStaff.get(`/crm/jobs/${jobId}`).then(r => setJob(r.data)); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={!!jobId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 capitalize">
            {job?.appliance} · {job?.customer_name}
            {job?.manager_flag && <Flag className="w-4 h-4 text-[#FF6600]" />}
          </DialogTitle>
        </DialogHeader>
        {!job ? <Loading /> : (
          <div className="space-y-5 text-sm">
            {/* Editable customer + appliance section (P0: every column editable) */}
            <div className="border border-[#002060]/15 rounded-lg p-3 bg-[#FFFFFF]" data-testid="drawer-customer-section">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#002060]/70 mb-2">Customer &amp; appliance</div>
              <div className="grid grid-cols-2 gap-3">
                <Mini label="Name"><Input data-testid="drawer-customer-name" defaultValue={job.customer_name} onBlur={(e) => e.target.value !== job.customer_name && update({ customer_name: e.target.value })} /></Mini>
                <Mini label="Phone"><Input data-testid="drawer-customer-phone" defaultValue={job.customer_phone} onBlur={(e) => e.target.value !== job.customer_phone && update({ customer_phone: e.target.value })} /></Mini>
                <div className="col-span-2"><Mini label="Email"><Input data-testid="drawer-customer-email" defaultValue={job.customer_email || ""} onBlur={(e) => e.target.value !== (job.customer_email || "") && update({ customer_email: e.target.value || null })} /></Mini></div>
                <Mini label="City"><Input data-testid="drawer-city" defaultValue={job.city || ""} onBlur={(e) => e.target.value !== (job.city || "") && update({ city: e.target.value || null })} /></Mini>
                <Mini label="Appliance">
                  <Select value={job.appliance} onValueChange={(v) => v !== job.appliance && update({ appliance: v })}>
                    <SelectTrigger data-testid="drawer-appliance"><SelectValue /></SelectTrigger>
                    <SelectContent>{["washer","dryer","refrigerator","oven","stove","dishwasher","microwave","disposal","ice-maker","other"].map(a => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}</SelectContent>
                  </Select>
                </Mini>
                <div className="col-span-2"><Mini label="Address"><Input data-testid="drawer-address" defaultValue={job.address} onBlur={(e) => e.target.value !== job.address && update({ address: e.target.value })} /></Mini></div>
                <Mini label="Model"><Input data-testid="drawer-model" defaultValue={job.appliance_model || ""} onBlur={(e) => e.target.value !== (job.appliance_model || "") && update({ appliance_model: e.target.value || null })} /></Mini>
                <div className="col-span-2">
                  <Mini label="Scheduled">
                    <SchedulePicker
                      value={job.scheduled_for || ""}
                      onChange={(iso) => {
                        if (iso !== (job.scheduled_for || "")) update({ scheduled_for: iso || null });
                      }}
                      testidPrefix="drawer-schedule"
                    />
                  </Mini>
                </div>
                <div className="col-span-2"><Mini label="Issue"><Textarea data-testid="drawer-issue" rows={2} defaultValue={job.issue || ""} onBlur={(e) => e.target.value !== (job.issue || "") && update({ issue: e.target.value || null })} /></Mini></div>
              </div>
            </div>
            <Mini label="Status">
              <Select value={job.status} onValueChange={(v) => update({ status: v })}>
                <SelectTrigger data-testid="drawer-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Mini>
            <Mini label="Assigned tech">
              <Select value={job.technician_id || ""} onValueChange={(v) => update({ technician_id: v === "__none__" ? null : v })}>
                <SelectTrigger data-testid="drawer-tech"><SelectValue placeholder="(unassigned)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Unassigned —</SelectItem>
                  {techs.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Mini>
            <Mini label="Private tech note">
              <Textarea data-testid="drawer-private-note" rows={3} defaultValue={job.private_tech_note || ""} onBlur={(e) => e.target.value !== (job.private_tech_note || "") && update({ private_tech_note: e.target.value })} />
            </Mini>
            {isManager && (
              <div className="bg-[#FFFFFF] -mx-6 px-6 py-4 border-y border-[#002060]/15">
                <div className="text-[11px] uppercase tracking-wider text-[#002060]/70 font-medium mb-3">Financials</div>
                <div className="grid grid-cols-3 gap-2">
                  <Mini label="Quoted"><Input data-testid="drawer-quoted" type="number" defaultValue={job.quoted_price || ""} onBlur={(e) => e.target.value !== "" && update({ quoted_price: parseFloat(e.target.value) })} /></Mini>
                  <Mini label="Parts"><Input data-testid="drawer-parts" type="number" defaultValue={job.parts_cost || ""} onBlur={(e) => e.target.value !== "" && update({ parts_cost: parseFloat(e.target.value) })} /></Mini>
                  <Mini label="Final"><Input data-testid="drawer-final" type="number" defaultValue={job.final_price || ""} onBlur={(e) => e.target.value !== "" && update({ final_price: parseFloat(e.target.value) })} /></Mini>
                </div>
                {latestInvoice ? (
                  <div className="mt-3" data-testid="drawer-payment-summary-wrap">
                    <PaymentSummary
                      summary={{
                        invoice_number: latestInvoice.invoice_number,
                        total: latestInvoice.total,
                        amount_paid: latestInvoice.amount_paid,
                        balance_due: latestInvoice.balance_due,
                        payment_type: latestInvoice.payment_type,
                        deposit_required: latestInvoice.deposit_amount_required,
                        status: latestInvoice.status,
                      }}
                      theme="light"
                      testid="drawer-payment-summary"
                    />
                  </div>
                ) : (
                  <div className="text-xs text-[#002060]/80 mt-2">Invoice: <span className="font-medium">{job.invoice_status || "—"}</span> {job.payment_method && `· via ${job.payment_method}`}</div>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button data-testid="drawer-cash" size="sm" variant="outline" className="h-8" onClick={() => recordManual("cash")}>Cash</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => recordManual("check")}>Check</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => recordManual("zelle")}>Zelle</Button>
                </div>
              </div>
            )}
            {/* Inline invoice manager */}
            <JobInvoicePanel
              job={job}
              role={isManager ? "manager" : "dispatcher"}
              onChanged={reloadInvoice}
              onView={(inv) => setViewInvoice(inv)}
            />
            <Button data-testid="drawer-flag" variant="outline" size="sm" onClick={flag} className="h-8"><Flag className="w-3.5 h-3.5 mr-1.5" /> Flag for manager</Button>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#002060]/70 font-medium mb-2">Timeline</div>
              <ul className="space-y-1 text-xs text-[#002060]">
                {(job.timeline || []).slice().reverse().map((t, i) => (
                  <li key={i}><span className="text-[#002060]/50">{new Date(t.ts).toLocaleString()}</span> · {t.event} <span className="text-[#002060]/50">by {t.by}</span></li>
                ))}
                {(!job.timeline || job.timeline.length === 0) && <li className="text-[#002060]/50 italic">No events yet.</li>}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
      {viewInvoice && (
        <Dialog open={!!viewInvoice} onOpenChange={(o) => !o && setViewInvoice(null)}>
          <DialogContent
            className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 bg-transparent border-0 shadow-none"
            aria-describedby={undefined}
          >
            <DialogTitle className="sr-only">Invoice {viewInvoice.invoice_number}</DialogTitle>
            <BrandedInvoice invoice={viewInvoice} onBack={() => setViewInvoice(null)} />
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
